import React, { useState, useEffect } from 'react';
import { User } from '../../../types';
import { Button, Checkbox, Select } from '../../common';
import { Modal } from '../../common/Modal';
import { useAdminTranslation } from '../../../modules/i18n';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingUser: User | null;
  onSave: (user: Partial<User>) => void;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, editingUser, onSave }) => {
  const { t } = useAdminTranslation();

  const [formData, setFormData] = useState<Partial<User>>({
    username: '',
    email: '',
    role: 'user',
    status: 'ACTIVE'
  });

  // Update form data when editingUser changes
  useEffect(() => {
    if (editingUser) {
      // Convert role from uppercase (database) to lowercase (UI)
      setFormData({
        username: editingUser.username || '',
        email: editingUser.email || '',
        firstName: editingUser.firstName || '',
        lastName: editingUser.lastName || '',
        role: editingUser.role?.toLowerCase() as 'admin' | 'user' | 'moderator',
        status: editingUser.status || 'ACTIVE'
      });
    }
  }, [editingUser]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert role to uppercase for backend compatibility
    const dataToSave = {
      ...formData,
      role: formData.role?.toUpperCase() as 'admin' | 'user' | 'moderator'
    };
    onSave(dataToSave);
    onClose();
  };

  if (!editingUser) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('content.users.modal.title')}
      size='sm'
      height='auto'
      footer={
        <div className='flex justify-end space-x-3'>
          <Button type='button' variant='ghost' onClick={onClose}>
            {t('content.users.modal.buttons.cancel')}
          </Button>
          <Button type='submit' variant='primary' onClick={handleSubmit}>
            {t('content.users.modal.buttons.update')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('content.users.modal.form.usernameLabel')}
          </label>
          <input
            type='text'
            value={formData.username || ''}
            readOnly
            className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
            title={t('content.users.modal.form.usernamePlaceholder')}
            placeholder={t('content.users.modal.form.usernamePlaceholder')}
          />
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('content.users.modal.form.emailLabel')}
          </label>
          <input
            type='email'
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            readOnly
            className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
            title={t('content.users.modal.form.emailPlaceholder')}
            placeholder={t('content.users.modal.form.emailPlaceholder')}
            required
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Tên</label>
            <input
              type='text'
              value={formData.firstName || ''}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              readOnly
              className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
              placeholder='Nhập tên'
            />
          </div>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Họ</label>
            <input
              type='text'
              value={formData.lastName || ''}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              readOnly
              className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
              placeholder='Nhập họ'
            />
          </div>
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('content.users.modal.form.roleLabel')}
          </label>
          <Select
            value={formData.role || 'user'}
            onChange={(value) => setFormData({ ...formData, role: value as 'admin' | 'user' | 'moderator' })}
            options={[
              { value: 'user', label: t('content.users.modal.roles.user') },
              { value: 'moderator', label: t('content.users.modal.roles.moderator') },
              { value: 'admin', label: t('content.users.modal.roles.admin') }
            ]}
          />
        </div>

        <Checkbox
          id='userStatus'
          checked={formData.status === 'ACTIVE' || false}
          onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'ACTIVE' : 'INACTIVE' })}
          label={t('content.users.modal.form.activeLabel')}
        />
      </form>
    </Modal>
  );
};

export default UserModal;
